import React from "react";
import { motion } from "framer-motion";
import { Pill, Beaker, Globe2, FlaskConical } from "lucide-react";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.25 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function APIs() {
  return (
    <div className="w-full">
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/apis-banner.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Active Pharmaceutical Ingredients (APIs)
        </h1>
      </div>

      {/* Content Section with Motion */}
      <motion.div
        className="max-w-6xl mx-auto px-4 py-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
      >
        {/* Intro */}
        <motion.p className="text-lg text-gray-700 mb-10" variants={fadeInUp}>
          At <span className="font-semibold text-[#0d47a1]">MAC LABS LTD</span>, we specialize in
          developing and manufacturing high-quality Active Pharmaceutical Ingredients that form the
          foundation of safe and effective medicines. With a strong emphasis on quality, consistency,
          and safety, our APIs serve as the building blocks for formulations that improve lives
          across the globe.
        </motion.p>

        {/* Our Strengths */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Our Strengths
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Motion cards with fade and hover */}
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Pill className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Quality First Approach</h3>
            <p className="text-gray-600">
              Every stage of production is driven by stringent quality assurance, ensuring purity,
              stability, and reliability.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Beaker className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Therapeutic Diversity</h3>
            <p className="text-gray-600">
              Covering a broad range of therapeutic categories – cardiovascular, CNS, respiratory,
              gastrointestinal, pain management, and more.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Globe2 className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Global Supply Capabilities</h3>
            <p className="text-gray-600">
              Scalable infrastructure and streamlined processes ensure uninterrupted worldwide
              supply.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <FlaskConical className="w-8 h-8 text-pink-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Innovation in Chemistry</h3>
            <p className="text-gray-600">
              Our R&D teams continuously optimize processes to deliver cost-effective and
              sustainable solutions.
            </p>
          </motion.div>
        </div>

        {/* Why Partner With Us */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Why Partner With Us?
        </motion.h2>
        <motion.ul
          className="list-disc list-inside text-gray-600 space-y-3 mb-10"
          variants={fadeInUp}
        >
          <li>Proven expertise in complex chemistry.</li>
          <li>Focus on safety, efficacy, and patient well-being.</li>
          <li>Flexible and scalable production capacity.</li>
          <li>Commitment to long-term, trust-based collaborations.</li>
        </motion.ul>
      </motion.div>
    </div>
  );
}
